/**
 * src/components/shared/TaskCard.jsx — Unified Task Card (v3 design)
 * AC Nautitech Design System
 *
 * Single task card component used across all views.
 * Accepts both raw Airtable records and FocusView mapped tasks.
 *
 * Variants:
 *   focus   — v3 card with priority dot, checkbox, no status chip
 *   project — v3 list row with priority dot, chevron, no status chip
 *   team    — minimal read-only (status, title, due)
 *   board   — kanban card (accent bar, status circle, project subtitle, blocked indicator, done-fade)
 *   myday   — flat list row with checkbox toggle, project subtitle, due date badge
 */

import { AlertCircle, ChevronRight, Edit3, ArrowRight, Copy, CheckCircle2, Circle, Tag, FileText } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { StatusChip, DueBadge, Avatar } from '../SharedUI';
import { resolvePersonName } from '../../lib/mockData';
import { priorityToP } from '../../lib/constants/priority';
import { isDone as isStatusDone, isBlocked as isStatusBlocked, isInProgress as isStatusInProgress } from '../../lib/constants/taskStatus';
import HoursBadge from './HoursBadge';
import { motion } from 'framer-motion';
import { ContextMenu, useCardActions } from './CardActions';
import { ServiceLabelStrip } from './CardPrimitives';
import { requestQuoteOpen, quoteIdFromTask } from '../../lib/nav/quoteNav';

/* Quote-review tasks open straight to their quote (CEO 2026-09-01); the tag
 * parser lives in quoteNav so every surface agrees (TICKET-1098). */
function OpenQuoteButton({ quoteId, tr }) {
  return (
    <button
      type="button"
      data-testid="task-card-open-quote"
      title={tr('taskCard.openQuote')}
      aria-label={tr('taskCard.openQuote')}
      onClick={(e) => { e.stopPropagation(); requestQuoteOpen(quoteId); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        minHeight: 44, padding: '8px 12px', margin: '-10px 0',
        background: 'var(--sprob, rgba(59,130,246,0.08))', color: 'var(--accent)',
        border: 'none', borderRadius: 8, fontSize: 11, fontWeight: 600,
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      <FileText size={12} /> {tr('taskCard.openQuote')}
    </button>
  );
}

/* ─── Priority colors ──────────────────────────────────
 * Match the FocusView filter pill colors so the badge on the card and the
 * filter button for the same priority show identical color treatment.
 * Uses var(--p1)/var(--p2)/var(--p3) design tokens.
 */
const P = {
  P1: { c: 'var(--p1)' },
  P2: { c: 'var(--p2)' },
  P3: { c: 'var(--p3)' },
};

/* ─── Status circle colors (board variant) ────────────── */
const STATUS_CIRCLE = {
  todo:        'var(--stodo, #94a3b8)',
  in_progress: 'var(--spro, #3b82f6)',
  wip:         'var(--spro, #3b82f6)',
  done:        'var(--sdn, #22c55e)',
  blocked:     'var(--sblk, #ef4444)',
};

/* ─── Accent bar color by project name heuristic ─────── */
const TYPE_ACCENT = {
  // HEX_TODO[#3b82f6] exception — see color-map.json
  refit:   '#3b82f6',
  // HEX_TODO[#6366f1] exception — see color-map.json
  new:     '#6366f1',
  // HEX_TODO[#8b5cf6] exception — see color-map.json
  design:  '#8b5cf6',
  service: 'var(--info)',
  // HEX_TODO[#14b8a6] exception — see color-map.json
  survey:  '#14b8a6',
};

function accentColor(projectName) {
  if (!projectName) return 'var(--tm)';
  const lower = projectName.toLowerCase();
  for (const [k, v] of Object.entries(TYPE_ACCENT)) {
    if (lower.includes(k)) return v;
  }
  // HEX_TODO[#3b82f6] exception — see color-map.json
  return '#3b82f6';
}

/* ─── Classify status into column key ────────────────── */
function classifyStatus(status) {
  if (isStatusInProgress(status)) return 'wip';
  if (isStatusDone(status)) return 'done';
  if (isStatusBlocked(status)) return 'blocked';
  return 'todo';
}

/* ─── Normalize task shape ────────────────────────────── */
function normalize(task, people) {
  // FocusView mapped shape (uses snake_case slugs already)
  if (task.title !== undefined) {
    return {
      id: task.id,
      title: task.title,
      status: task.st || 'todo',
      priority: task.pr,
      area: task.area,
      assignee: task.who,
      due: task.due,
      estimatedHours: task.budgetHours || 0,
      actualHours: task.actualHours || 0,
      department: task.area,
      projectName: task.projectName || '',
      updatedAt: null,
      dealId: task.dealId || task.deal_id || null,
    };
  }
  // FIX[wave-2]: raw tasks row per SHAPE_CONTRACT
  // priorityToP normalizes DB 'high'/'medium'/'low' → 'P1'/'P2'/'P3' so the
  // badge lookup and sort order work on raw rows too.
  return {
    id: task.id,
    title: task.task_name || 'Untitled',
    status: task.status || 'todo',
    priority: task.priority ? priorityToP(task.priority) : null,
    area: null,
    assignee: task.user_profiles?.display_name || resolvePersonName(task.assignee_id, people),
    due: task.due_date,
    estimatedHours: task.estimated_hours || 0,
    actualHours: task.actual_hours || 0,
    department: task.department,
    projectName: task.projects?.project_name || '',
    updatedAt: task.updated_at || task.created_at || null,
    // TICKET-PIPELINE-TRACE S4: pipeline origin. Set when the task was created
    // from a deal voice note (and kept after promotion for provenance).
    dealId: task.deal_id || null,
  };
}

/* ─── Pipeline-origin chip (TICKET-PIPELINE-TRACE S4) ───
 * Marks a task that originated from a pipeline deal so it stays traceable to
 * its CRM deal even after the deal is promoted to a project. Tokens only;
 * mirrors the department badge treatment (accent text on a soft tint). */
function PipelineChip({ label }) {
  return (
    <span
      data-testid="task-from-pipeline"
      title={label}
      style={{
        fontSize: 9, fontWeight: 600, color: 'var(--accent)',
        background: 'var(--sprob, rgba(59,130,246,0.08))',
        padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 3,
      }}
    >
      <Tag size={9} /> {label}
    </span>
  );
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

/* ─── Format date for display ────────────────────────── */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/* ─── TaskCard Component ──────────────────────────────── */
export default function TaskCard({
  task,
  displayTitle, // TICKET-959: the title in the viewer's content language (display only)
  variant = 'project',
  people = [],
  onClick,
  onStatusChange,
  onStatusSelect,
  onCycleStatus,
  onArchive,
  onToggle,
  projectLookup,
}) {
  const { t: tr } = useT();
  // Hooks unconditionally — used by 'focus' variant only, but Rules of Hooks
  // require consistent call order across renders.
  const { menuPos, onContextMenu, closeMenu } = useCardActions();
  const t0 = normalize(task, people);
  const t = displayTitle ? { ...t0, title: displayTitle } : t0;
  const quoteId = quoteIdFromTask(task);
  const isDone = isStatusDone(t.status);
  const isOverdue = t.due && t.due < todayStr() && !isDone;
  const overBudget = t.estimatedHours > 0 && t.actualHours > t.estimatedHours;

  /* ── Team variant (unchanged) ────────────────────────── */
  if (variant === 'team') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
        borderBottom: '1px solid var(--bdr)',
      }}>
        <StatusChip status={t.status} />
        <div style={{ flex: 1, fontSize: 13, color: 'var(--t1)' }}>{t.title}</div>
        {t.due && <DueBadge dueDate={t.due} />}
      </div>
    );
  }

  /* ── Focus variant (v3 card) ─────────────────────────── */
  if (variant === 'focus') {
    return (
      <>
        <motion.div
          layout
          data-testid="task-card"
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
          onContextMenu={onContextMenu}
          className="group relative cursor-pointer overflow-hidden"
          style={{
            borderRadius: 10,
            background: 'var(--card)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            // TICKET-1098: quote-review tasks read as their own lane (amber = review, same as Pipeline).
            border: quoteId ? '1px solid var(--warn)' : '1px solid rgba(0,0,0,0.10)',
            transition: 'transform 150ms ease, box-shadow 150ms ease',
            touchAction: 'manipulation',
            overflow: 'hidden',
          }}
          transition={{ layout: { duration: 0.25, ease: 'easeOut' } }}
          whileHover={{ y: -1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
          whileTap={{ scale: 0.98, backgroundColor: 'var(--bg)' }}
        >
          {/* Left color stripe — reflects priority (P1 red / P2 amber / P3 green).
              Overdue is already indicated via the date text color in the metadata
              row, so the stripe stays priority-first to match the filter pill
              color scheme the user sees above the board. */}
          <div
            className="absolute"
            style={{
              top: 4,
              left: 4,
              bottom: 4,
              width: 3,
              borderRadius: 2,
              background: quoteId ? 'var(--warn)' : (P[t.priority]?.c || 'var(--p2)'),
            }}
          />

          <div className="flex">
            {/* Card body — extra left padding for color stripe */}
            <div
              className="flex-1"
              style={{
                padding: '12px 16px 12px 14px',
                opacity: isDone ? 0.4 : 1,
              }}
            >
              {/* Title row: priority dot + title */}
              <div className="flex items-start gap-1.5">
                {(t.priority === 'P1' || t.priority === 'P2') && (
                  <div
                    className="shrink-0 mt-[5px]"
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: P[t.priority]?.c,
                    }}
                  />
                )}
                <div
                  className={`flex-1 leading-tight ${isDone ? 'line-through' : ''}`}
                  style={{ fontSize: 13, fontWeight: 500, color: isDone ? 'var(--tm)' : 'var(--navy)' }}
                >
                  {t.title}
                </div>
              </div>

              {/* Metadata row: department + date + over-budget + avatar */}
              <div className="flex items-center mt-1.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {t.department && (
                    <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--accent)', background: 'var(--sprob, rgba(59,130,246,0.08))', padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                      {t.department}
                    </span>
                  )}
                  {t.due && (
                    <span style={{ fontSize: 10, fontWeight: 500, color: isOverdue ? 'var(--bad)' : 'var(--tm)' }}>
                      {formatDate(t.due)}
                    </span>
                  )}
                  {overBudget && (
                    <HoursBadge actual={t.actualHours} planned={t.estimatedHours} size="sm" />
                  )}
                </div>
                {t.assignee && (
                  <span className="shrink-0" title={t.assignee}>
                    <Avatar name={t.assignee} size={18} />
                  </span>
                )}
              </div>
              {quoteId && (
                <div className="mt-1.5"><OpenQuoteButton quoteId={quoteId} tr={tr} /></div>
              )}
            </div>

            {/* Checkbox toggle — tighter footprint. Was w-10 (40px) outer +
                w-7 (28px) visual circle which overflowed the card on mobile
                narrow columns and got clipped by the card's overflow:hidden.
                Now 36px outer hit zone with paddingRight so the visual edge
                sits inside the card's 14px right padding cleanly on all
                viewports. Circle shrunk to 22px with 12x12 check icon. */}
            {onCycleStatus && (
              <div
                role="button"
                tabIndex={0}
                onClick={e => { e.stopPropagation(); onCycleStatus(t.id); }}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onCycleStatus(t.id); } }}
                className="min-h-[44px] flex items-center justify-center shrink-0 cursor-pointer active:scale-90 transition-transform z-20 relative"
                style={{ width: 36, paddingRight: 10 }}
              >
                <motion.div
                  animate={{ scale: isDone ? [1, 1.2, 1] : 1 }}
                  transition={{ duration: 0.25 }}
                  className={`rounded-full flex items-center justify-center ${isDone ? 'bg-ok' : 'border-2 border-bdr bg-transparent'}`}
                  style={{ width: 22, height: 22 }}
                >
                  {isDone && (
                    <svg width="12" height="12" viewBox="0 0 12 12">
                      <path d="M2 6.5L5 9.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
                    </svg>
                  )}
                </motion.div>
              </div>
            )}
          </div>
        </motion.div>
        <ContextMenu pos={menuPos} onClose={closeMenu} actions={[
          { icon: Edit3, label: tr('taskCard.openTask'), onClick: () => onClick?.() },
          { icon: ArrowRight, label: tr('taskCard.changeStatus'), onClick: () => onCycleStatus?.(t.id) },
          { divider: true },
          { icon: Copy, label: tr('taskCard.copyName'), onClick: () => navigator.clipboard?.writeText(t.title) },
          { label: tr('taskCard.copyLink'), onClick: () => navigator.clipboard?.writeText(`${window.location.origin}/task/${t.id}`) },
          { divider: true },
          { label: tr('taskCard.archive'), onClick: () => onArchive?.(task?.id || t.id) },
        ]} />
      </>
    );
  }

  /* ── Board variant (gantt/TaskBoard style) ───────────── */
  if (variant === 'board') {
    const statusKey = classifyStatus(t.status);
    const isBlocked = statusKey === 'blocked';
    const statusCircleColor = STATUS_CIRCLE[t.status] || STATUS_CIRCLE[statusKey] || 'var(--tm)';
    const projectName = t.projectName || (task.project_id && projectLookup ? (projectLookup[task.project_id] || '') : '');
    const doneAt = t.updatedAt;
    const daysDone = isDone && doneAt
      ? Math.floor((Date.now() - new Date(doneAt).getTime()) / 86400000)
      : 0;
    const isFading = isDone && daysDone > 7;

    return (
      <>
        <motion.div
          data-testid="task-card"
          className="group relative cursor-grab"
          onContextMenu={onContextMenu}
          onClick={onClick}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            background: 'var(--card)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.10)',
            borderLeft: isBlocked ? '3px solid var(--bad)' : '1px solid rgba(0,0,0,0.10)',
            transition: 'transform 150ms ease, box-shadow 150ms ease',
            opacity: isFading ? 0.45 : 1,
            touchAction: 'none',
          }}
          whileHover={{ y: -1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
          {/* 2px accent bar */}
          <div
            className="absolute top-0 left-0 right-0"
            style={{
              height: 2,
              borderRadius: '10px 10px 0 0',
              background: isBlocked ? 'var(--bad)' : accentColor(projectName),
            }}
          />

          {/* Title row: status circle + priority dot + title */}
          <div className="flex items-start" style={{ gap: 6, paddingTop: 4 }}>
            <div
              className="shrink-0"
              style={{
                width: 8, height: 8, borderRadius: '50%',
                background: statusCircleColor, flexShrink: 0, marginTop: 5,
              }}
              title={statusKey}
            />
            {(t.priority === 'P1' || t.priority === 'P2') && (
              <div
                className="shrink-0"
                style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: P[t.priority]?.c, marginTop: 6,
                }}
                title={t.priority}
              />
            )}
            <div
              className="flex-1 leading-tight"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}
            >
              {t.title}
            </div>
          </div>

          {/* Project subtitle */}
          {projectName && (
            <div
              className="mt-1 leading-tight truncate"
              style={{ fontSize: 11, fontWeight: 400, color: 'var(--tm)' }}
            >
              {projectName}
            </div>
          )}

          {/* Pipeline origin (TICKET-PIPELINE-TRACE S4) */}
          {t.dealId && (
            <div className="mt-1">
              <PipelineChip label={tr('taskCard.fromPipeline')} />
            </div>
          )}

          {/* Blocked indicator */}
          {isBlocked && (
            <div
              className="mt-1 flex items-center gap-1"
              style={{ fontSize: 10, fontWeight: 500, color: 'var(--bad)' }}
            >
              <AlertCircle size={10} />
              Blocked
            </div>
          )}

          {/* Metadata row: date + over-budget + auto-archive hint + avatar */}
          <div className="flex items-center mt-1.5">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {t.due && (
                <span style={{ fontSize: 10, fontWeight: 500, color: isOverdue ? 'var(--bad)' : 'var(--tm)' }}>
                  {formatDate(t.due)}
                </span>
              )}
              {overBudget && (
                <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--bad)' }}>
                  {Math.round(t.actualHours)}/{Math.round(t.estimatedHours)}h
                </span>
              )}
              {isFading && (
                <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--tm)', fontStyle: 'italic' }}>auto-archive soon</span>
              )}
            </div>
            {t.assignee && (
              <span className="shrink-0" title={t.assignee}>
                <Avatar name={t.assignee} size={18} />
              </span>
            )}
          </div>
          {quoteId && (
            <div className="mt-1.5"><OpenQuoteButton quoteId={quoteId} tr={tr} /></div>
          )}
        </motion.div>
        <ContextMenu pos={menuPos} onClose={closeMenu} actions={[
          { icon: Edit3, label: tr('taskCard.openTask'), onClick: () => onClick?.() },
          { divider: true },
          { icon: Copy, label: tr('taskCard.copyName'), onClick: () => navigator.clipboard?.writeText(t.title) },
          { label: tr('taskCard.copyLink'), onClick: () => navigator.clipboard?.writeText(`${window.location.origin}/task/${t.id}`) },
          { divider: true },
          { label: tr('taskCard.archive'), onClick: () => onArchive?.(task?.id || t.id) },
        ]} />
      </>
    );
  }

  /* ── MyDay variant (flat list row with checkbox) ────── */
  if (variant === 'myday') {
    const projectName = t.projectName || (task.project_id && projectLookup ? (projectLookup[task.project_id] || '') : '');

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 12px',
          minHeight: 44,
          cursor: 'pointer',
        }}
      >
        {/* Checkbox — 44x44 touch target */}
        <button
          onClick={e => { e.stopPropagation(); onToggle?.(t.id, t.status); }}
          aria-label={isDone ? tr('myday.markIncomplete') : tr('myday.markComplete')}
          style={{
            flexShrink: 0, width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', cursor: 'pointer',
            borderRadius: '50%', padding: 0, border: 'none',
          }}
        >
          {isDone
            ? <CheckCircle2 size={20} style={{ color: 'var(--green, #22c55e)' }} />
            : <Circle size={20} strokeWidth={1.5} style={{ color: 'var(--tm)' }} />
          }
        </button>

        {/* Task info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 500,
            color: isDone ? 'var(--tm)' : 'var(--t1)',
            textDecoration: isDone ? 'line-through' : 'none',
            lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {t.title}
          </div>
          {projectName && (
            <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
              {projectName}
            </div>
          )}
        </div>

        {/* Open quote — one-click link for review tasks */}
        {quoteId && <OpenQuoteButton quoteId={quoteId} tr={tr} />}

        {/* Due date badge */}
        {t.due && (
          <span style={{
            fontSize: 11, fontWeight: 500, flexShrink: 0,
            color: isOverdue ? 'var(--red, #ef4444)' : 'var(--tm)',
            padding: '2px 6px', borderRadius: 6,
            background: isOverdue ? 'rgba(239, 68, 68, 0.08)' : 'transparent',
          }}>
            {formatDate(t.due)}
          </span>
        )}
      </div>
    );
  }

  /* -- Project variant (v3 list row) -- */
  // One-tap close: the leading control is a real checkbox, not the old
  // ambiguous gray dot that cycled todo->in_progress->blocked->done (3 taps to
  // close, no done feedback). Tapping toggles done<->todo directly via
  // onStatusSelect (projects/TasksTab wires it to handleUpdateStatus). When a
  // consumer passes only onStatusChange (pipeline TasksTab, no direct setter),
  // we fall back to its cycle handler so nothing regresses there.
  // CEO 2026-06-15: "click on a task to close it" + done must visibly leave the
  // open list (strikethrough here + parent TasksTab hides completed by default).
  const toggleDone = (e) => {
    e.stopPropagation();
    if (onStatusSelect) onStatusSelect(t.id, isDone ? 'todo' : 'done');
    else if (onStatusChange) onStatusChange(t.id, t.status);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      className="group flex items-center gap-2.5 cursor-pointer transition-all duration-150 hover:bg-[rgba(0,0,0,0.02)]"
      style={{
        padding: '12px 16px',
        borderRadius: 10,
        background: 'var(--card)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.10)',
        marginBottom: 4,
        opacity: isDone ? 0.6 : 1,
      }}
    >
      {(onStatusSelect || onStatusChange) && (
        <button
          type="button"
          data-testid="project-variant-status-toggle"
          aria-label={isDone ? tr('myday.markIncomplete') : tr('myday.markComplete')}
          onClick={toggleDone}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleDone(e); }}
          className="shrink-0 active:scale-90 transition-transform"
          style={{
            width: 44, height: 44, minWidth: 44, minHeight: 44,
            margin: '-12px 0 -12px -8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          }}
        >
          {isDone
            ? <CheckCircle2 size={20} style={{ color: 'var(--ok)' }} />
            : <Circle size={20} strokeWidth={1.5} style={{ color: 'var(--tm)' }} />}
        </button>
      )}

      {(t.priority === 'P1' || t.priority === 'P2') && !isDone && (
        <div
          className="shrink-0"
          style={{ width: 5, height: 5, borderRadius: '50%', background: P[t.priority]?.c }}
        />
      )}

      <div className="flex-1 min-w-0">
        <div
          data-testid="project-task-title"
          className="leading-tight truncate"
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: isDone ? 'var(--tm)' : 'var(--navy)',
            textDecoration: isDone ? 'line-through' : 'none',
          }}
        >
          {t.title}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {t.assignee && t.assignee !== 'Unassigned' && (
            <span className="shrink-0" title={t.assignee}>
              <Avatar name={t.assignee} size={18} />
            </span>
          )}
          {t.due && (
            <span style={{ fontSize: 10, fontWeight: 500, color: isOverdue ? 'var(--bad)' : 'var(--tm)' }}>
              {formatDate(t.due)}
            </span>
          )}
          {t.dealId && <PipelineChip label={tr('taskCard.fromPipeline')} />}
          {quoteId && <OpenQuoteButton quoteId={quoteId} tr={tr} />}
        </div>
      </div>

      {overBudget && (
        <HoursBadge actual={t.actualHours} planned={t.estimatedHours} size="sm" />
      )}
      <ChevronRight size={14} color="var(--tm)" />
    </div>
  );
}
